
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {MarginRateCalculationType} from './MarginRateCalculationType';
import {FieldModelMarginRateCalculationType} from './MarginRateCalculationType';
import {TradingMode} from './TradingMode';
import {FieldModelTradingMode} from './TradingMode';

/**
 * SymbolGroup struct
 */
class SymbolGroup {
  /**
   * Initialize struct
   * @param {!UUID=} id
   * @param {!number=} sortOrder
   * @param {!string=} name
   * @param {!string=} comment
   * @param {!string=} description
   * @param {!number=} color
   * @param {!boolean=} isTradeAllowed
   * @param {!number=} defaultExpiration
   * @param {!MarginRateCalculationType=} marginRateCalculationType
   * @param {!TradingMode=} defaultTradingMode
   * @param {!number=} index
   * @param {string=} clientName
   * @constructor
   */
  constructor (id = UUID.sequential(), sortOrder = 0, name = '', comment = '', description = '', color = 0, isTradeAllowed = false, defaultExpiration = new UInt64(0, 0), marginRateCalculationType = new MarginRateCalculationType(), defaultTradingMode = new TradingMode(), index = 0, clientName = null) {
    this.id = id
    this.SortOrder = sortOrder
    this.Name = name
    this.Comment = comment
    this.Description = description
    this.Color = color
    this.IsTradeAllowed = isTradeAllowed
    this.DefaultExpiration = defaultExpiration
    this.MarginRateCalculationType = marginRateCalculationType
    this.DefaultTradingMode = defaultTradingMode
    this.Index = index
    this.ClientName = clientName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SymbolGroup}
   * @param {!SymbolGroup} other Other struct
   * @returns {!SymbolGroup} This struct
   */
  copy (other) {
    if (other.id != null) {
      this.id = new UUID(other.id)
    } else {
      this.id = null
    }
    if (other.SortOrder != null) {
      this.SortOrder = other.SortOrder
    } else {
      this.SortOrder = null
    }
    if (other.Name != null) {
      this.Name = other.Name
    } else {
      this.Name = null
    }
    if (other.Comment != null) {
      this.Comment = other.Comment
    } else {
      this.Comment = null
    }
    if (other.Description != null) {
      this.Description = other.Description
    } else {
      this.Description = null
    }
    if (other.Color != null) {
      this.Color = other.Color
    } else {
      this.Color = null
    }
    if (other.IsTradeAllowed != null) {
      this.IsTradeAllowed = other.IsTradeAllowed
    } else {
      this.IsTradeAllowed = null
    }
    if (other.DefaultExpiration != null) {
      this.DefaultExpiration = UInt64.fromNumber(other.DefaultExpiration)
    } else {
      this.DefaultExpiration = null
    }
    if (other.MarginRateCalculationType != null) {
      this.MarginRateCalculationType = MarginRateCalculationType.fromObject(other.MarginRateCalculationType)
    } else {
      this.MarginRateCalculationType = null
    }
    if (other.DefaultTradingMode != null) {
      this.DefaultTradingMode = TradingMode.fromObject(other.DefaultTradingMode)
    } else {
      this.DefaultTradingMode = null
    }
    if (other.Index != null) {
      this.Index = other.Index
    } else {
      this.Index = null
    }
    if (other.ClientName != null) {
      this.ClientName = other.ClientName
    } else {
      this.ClientName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SymbolGroup}
   * @returns {!SymbolGroup} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SymbolGroupModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SymbolGroupModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SymbolGroup}
   * @param {!SymbolGroup} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SymbolGroup)) {
      throw new TypeError('Instance of SymbolGroup is required!')
    }
    // noinspection RedundantIfStatementJS
    if (this.id != null) {
      if ((other.id == null) || !this.id.eq(other.id)) {
        return false
      }
    } else if (other.id != null) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SymbolGroup}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      id: ((this.id != null) ? this.id.toString() : null),
      SortOrder: ((this.SortOrder != null) ? this.SortOrder : null),
      Name: ((this.Name != null) ? this.Name : null),
      Comment: ((this.Comment != null) ? this.Comment : null),
      Description: ((this.Description != null) ? this.Description : null),
      Color: ((this.Color != null) ? this.Color : null),
      IsTradeAllowed: ((this.IsTradeAllowed != null) ? this.IsTradeAllowed : null),
      DefaultExpiration: ((this.DefaultExpiration != null) ? this.DefaultExpiration.toNumber() : null),
      MarginRateCalculationType: ((this.MarginRateCalculationType != null) ? this.MarginRateCalculationType : null),
      DefaultTradingMode: ((this.DefaultTradingMode != null) ? this.DefaultTradingMode : null),
      Index: ((this.Index != null) ? this.Index : null),
      ClientName: ((this.ClientName != null) ? this.ClientName : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SymbolGroup.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SymbolGroup} other Object value
   * @returns {!SymbolGroup} Created struct
   */
  static fromObject (other) {
    return new SymbolGroup().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SymbolGroup}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SymbolGroup.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SymbolGroup}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 19
  }
}

export { SymbolGroup };

/**
 * Fast Binary Encoding SymbolGroup field model
 */
class FieldModelSymbolGroup extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.id = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.SortOrder = new fbe.FieldModelUInt32(buffer, this.id.fbeOffset + this.id.fbeSize)
    this.Name = new fbe.FieldModelString(buffer, this.SortOrder.fbeOffset + this.SortOrder.fbeSize)
    this.Comment = new fbe.FieldModelString(buffer, this.Name.fbeOffset + this.Name.fbeSize)
    this.Description = new fbe.FieldModelString(buffer, this.Comment.fbeOffset + this.Comment.fbeSize)
    this.Color = new fbe.FieldModelUInt32(buffer, this.Description.fbeOffset + this.Description.fbeSize)
    this.IsTradeAllowed = new fbe.FieldModelBool(buffer, this.Color.fbeOffset + this.Color.fbeSize)
    this.DefaultExpiration = new fbe.FieldModelUInt64(buffer, this.IsTradeAllowed.fbeOffset + this.IsTradeAllowed.fbeSize)
    this.MarginRateCalculationType = new FieldModelMarginRateCalculationType(buffer, this.DefaultExpiration.fbeOffset + this.DefaultExpiration.fbeSize)
    this.DefaultTradingMode = new FieldModelTradingMode(buffer, this.MarginRateCalculationType.fbeOffset + this.MarginRateCalculationType.fbeSize)
    this.Index = new fbe.FieldModelUInt16(buffer, this.DefaultTradingMode.fbeOffset + this.DefaultTradingMode.fbeSize)
    this.ClientName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.Index.fbeOffset + this.Index.fbeSize), buffer, this.Index.fbeOffset + this.Index.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelSymbolGroup}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSymbolGroup}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.id.fbeSize + this.SortOrder.fbeSize + this.Name.fbeSize + this.Comment.fbeSize + this.Description.fbeSize + this.Color.fbeSize + this.IsTradeAllowed.fbeSize + this.DefaultExpiration.fbeSize + this.MarginRateCalculationType.fbeSize + this.DefaultTradingMode.fbeSize + this.Index.fbeSize + this.ClientName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSymbolGroup}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.id.fbeExtra + this.SortOrder.fbeExtra + this.Name.fbeExtra + this.Comment.fbeExtra + this.Description.fbeExtra + this.Color.fbeExtra + this.IsTradeAllowed.fbeExtra + this.DefaultExpiration.fbeExtra + this.MarginRateCalculationType.fbeExtra + this.DefaultTradingMode.fbeExtra + this.Index.fbeExtra + this.ClientName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSymbolGroup}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSymbolGroup.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSymbolGroup}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 19
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSymbolGroup}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSymbolGroup}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.SortOrder.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SortOrder.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SortOrder.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Name.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Comment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Description.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.Color.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Color.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Color.fbeSize

    if ((fbeCurrentSize + this.IsTradeAllowed.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsTradeAllowed.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsTradeAllowed.fbeSize

    if ((fbeCurrentSize + this.DefaultExpiration.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DefaultExpiration.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DefaultExpiration.fbeSize

    if ((fbeCurrentSize + this.MarginRateCalculationType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarginRateCalculationType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginRateCalculationType.fbeSize

    if ((fbeCurrentSize + this.DefaultTradingMode.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DefaultTradingMode.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DefaultTradingMode.fbeSize

    if ((fbeCurrentSize + this.Index.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Index.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Index.fbeSize

    if ((fbeCurrentSize + this.ClientName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ClientName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ClientName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSymbolGroup}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSymbolGroup}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSymbolGroup}
   * @param {!SymbolGroup} fbeValue Default value, defaults is new SymbolGroup()
   * @returns {!SymbolGroup} SymbolGroup value
   */
  get (fbeValue = new SymbolGroup()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSymbolGroup}
   * @param {!SymbolGroup} fbeValue SymbolGroup value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) <= fbeStructSize) {
      fbeValue.id = this.id.get(UUID.sequential())
    } else {
      fbeValue.id = UUID.sequential()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.SortOrder.fbeSize) <= fbeStructSize) {
      fbeValue.SortOrder = this.SortOrder.get()
    } else {
      fbeValue.SortOrder = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SortOrder.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) <= fbeStructSize) {
      fbeValue.Name = this.Name.get()
    } else {
      fbeValue.Name = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) <= fbeStructSize) {
      fbeValue.Comment = this.Comment.get()
    } else {
      fbeValue.Comment = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) <= fbeStructSize) {
      fbeValue.Description = this.Description.get()
    } else {
      fbeValue.Description = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.Color.fbeSize) <= fbeStructSize) {
      fbeValue.Color = this.Color.get()
    } else {
      fbeValue.Color = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Color.fbeSize

    if ((fbeCurrentSize + this.IsTradeAllowed.fbeSize) <= fbeStructSize) {
      fbeValue.IsTradeAllowed = this.IsTradeAllowed.get()
    } else {
      fbeValue.IsTradeAllowed = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsTradeAllowed.fbeSize

    if ((fbeCurrentSize + this.DefaultExpiration.fbeSize) <= fbeStructSize) {
      fbeValue.DefaultExpiration = this.DefaultExpiration.get()
    } else {
      fbeValue.DefaultExpiration = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DefaultExpiration.fbeSize

    if ((fbeCurrentSize + this.MarginRateCalculationType.fbeSize) <= fbeStructSize) {
      fbeValue.MarginRateCalculationType = this.MarginRateCalculationType.get()
    } else {
      fbeValue.MarginRateCalculationType = new MarginRateCalculationType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginRateCalculationType.fbeSize

    if ((fbeCurrentSize + this.DefaultTradingMode.fbeSize) <= fbeStructSize) {
      fbeValue.DefaultTradingMode = this.DefaultTradingMode.get()
    } else {
      fbeValue.DefaultTradingMode = new TradingMode()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DefaultTradingMode.fbeSize

    if ((fbeCurrentSize + this.Index.fbeSize) <= fbeStructSize) {
      fbeValue.Index = this.Index.get()
    } else {
      fbeValue.Index = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Index.fbeSize

    if ((fbeCurrentSize + this.ClientName.fbeSize) <= fbeStructSize) {
      fbeValue.ClientName = this.ClientName.get()
    } else {
      fbeValue.ClientName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ClientName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSymbolGroup}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSymbolGroup}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSymbolGroup}
   * @param {!SymbolGroup} fbeValue SymbolGroup value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSymbolGroup}
   * @param {SymbolGroup} fbeValue SymbolGroup value
   */
  setFields (fbeValue) {
    this.id.set(fbeValue.id)
    this.SortOrder.set(fbeValue.SortOrder)
    this.Name.set(fbeValue.Name)
    this.Comment.set(fbeValue.Comment)
    this.Description.set(fbeValue.Description)
    this.Color.set(fbeValue.Color)
    this.IsTradeAllowed.set(fbeValue.IsTradeAllowed)
    this.DefaultExpiration.set(fbeValue.DefaultExpiration)
    this.MarginRateCalculationType.set(fbeValue.MarginRateCalculationType)
    this.DefaultTradingMode.set(fbeValue.DefaultTradingMode)
    this.Index.set(fbeValue.Index)
    this.ClientName.set(fbeValue.ClientName)
  }
}

export { FieldModelSymbolGroup };

/**
 * Fast Binary Encoding SymbolGroup model
 */
class SymbolGroupModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSymbolGroup(this.buffer, 4)
  }

  /**
   * Get the SymbolGroup model
   * @this {!SymbolGroupModel}
   * @returns {!FieldModelSymbolGroup} model SymbolGroup model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SymbolGroupModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SymbolGroupModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SymbolGroupModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SymbolGroupModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSymbolGroup.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SymbolGroupModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SymbolGroupModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SymbolGroupModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SymbolGroupModel}
   * @param {!SymbolGroup} value SymbolGroup value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SymbolGroupModel}
   * @param {!SymbolGroup} value SymbolGroup value, defaults is new SymbolGroup()
   * @return {!object} Deserialized SymbolGroup value and its size
   */
  deserialize (value = new SymbolGroup()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SymbolGroup(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SymbolGroup(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SymbolGroupModel}
   * @param {!number} prev Previous SymbolGroup model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SymbolGroupModel };
